import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import Api from '../api/Api';
import useHomeStore from "../store/homeStore";
import { useMarkerStore } from "../store/markerStore";
import { INotificationResponse } from "../mock/Notifications";
import { QUERY_KEYS } from "./queryKeys";
import {SEARCHPARAM} from "../utils/common";

export const useNotificationsQuery = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.home.notification],
    queryFn: async () => {
      const notifications = await Api.notifications.getNotifications();
      return notifications.map((notification: INotificationResponse) => {
        return {
          ...notification,
          id: notification.guid,
          time: new Date(notification.datetime).getTime(),
          hasLink: notification.link ? true : false,
          message: notification.text,
        };
      });
    }
  });
};

export const useBsApiQuery = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.home.bsApi],
    queryFn: async () => {
        const [companySizeData, segmentsData, countryData] = await Promise.all([
          Api.bsApi.getCompanySizeInfo(),
          Api.bsApi.getSegmentsInfo(),
          Api.bsApi.getPriceCountryList()
        ]);

        return {
          companySizeData,
          segmentsData,
          countryData
        };
    }
  });
};

export const useUrlParamsQuery = () => {
  const location = useLocation();

  return useQuery({
    queryKey: ['checkUrlParams', location.search],
    queryFn: () => {
      const searchParams = new URLSearchParams(location.search);
      const coordinate = searchParams.get(SEARCHPARAM.COOR);

      return { hasCoordinate: !!coordinate };
    },
  });
};

export const useNotificationsBsQuery = () => {
  const setCompanySizeList = useHomeStore(state => state.setCompanySizeList);
  const setSegmentsList = useHomeStore(state => state.setSegmentsList);
  const setCountryList = useHomeStore(state => state.setCountryList);
  const setNotifications = useHomeStore(state => state.setNotifications);
  const setAddMarkerMode = useMarkerStore(state => state.setAddMarkerMode);
  const markers = useMarkerStore(state => state.markers);

  const notificationsQuery = useNotificationsQuery();
  const bsApiQuery = useBsApiQuery();
  const urlParamsQuery = useUrlParamsQuery();

  useEffect(() => {
    if (notificationsQuery.data) {
      setNotifications(notificationsQuery.data);
    }
  }, [notificationsQuery.data, setNotifications]);

  useEffect(() => {
    if (bsApiQuery.data) {
      const { companySizeData, segmentsData, countryData } = bsApiQuery.data;
      setCompanySizeList(companySizeData);
      setSegmentsList(segmentsData);
      setCountryList(countryData);
    }
  }, [bsApiQuery.data, setCompanySizeList, setSegmentsList, setCountryList]);

  useEffect(() => {
    if (urlParamsQuery.data && !urlParamsQuery.data.hasCoordinate && markers.length === 0) {
      setAddMarkerMode(true);
    }
  }, [urlParamsQuery.data, markers.length , setAddMarkerMode]);

  return {
    notificationsData: notificationsQuery.data,
    bsApiData: bsApiQuery.data,
    urlParamsData: urlParamsQuery.data,
    isLoading: notificationsQuery.isLoading || bsApiQuery.isLoading || urlParamsQuery.isLoading,
    isError: notificationsQuery.isError || bsApiQuery.isError || urlParamsQuery.isError
  };
};
